import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import {
    ISurveysFeatureState,
    SURVEY_QUESTION_RESPONSE_OPTIONS,
    Survey,
    SurveysProvider,
    dismissSurvey,
    saveResponse,
} from '@modeso/aumico-lib-core-fe';
import { ISurveyResponses } from '@modeso/aumico-lib-core-fe/interfaces/states/surveys-feature-state.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import cloneDeep from 'lodash/cloneDeep';
import { filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'app-survey-popup',
    templateUrl: 'survey-popup.component.html',
    styleUrls: ['./survey-popup.component.scss'],
})
export class SurveyPopupComponent implements OnInit {
    currentQuestion$ = this.store.select((state) => state.currentQuestion);

    public surveyQuestionResponseOptions = SURVEY_QUESTION_RESPONSE_OPTIONS;
    private activeSurvey: Survey | undefined;
    public currentQuestion = 1;
    public responses: ISurveyResponses;
    public response: SURVEY_QUESTION_RESPONSE_OPTIONS | string;
    public surveyForm: FormGroup;

    @Output() closeButtonClicked = new EventEmitter<void>();

    constructor(
        private store: Store<ISurveysFeatureState>,
        private provider: SurveysProvider,
        private fb: FormBuilder,
        private readonly actionsListener$: ActionsSubject,
    ) {}

    ngOnInit() {
        this.initializeForm();
        this.provider.showSurveyPopup();

        this.provider
            .getActiveSurvey$()
            .pipe(
                filter((survey) => survey != null),
                untilDestroyed(this),
            )
            .subscribe((survey) => {
                this.activeSurvey = cloneDeep(survey);
            });

        this.provider.dispatchGetActiveSurvey();

        this.provider
            .getCurrentQuestion$()
            .pipe(untilDestroyed(this))
            .subscribe((currentQuestion) => {
                this.currentQuestion = currentQuestion;
            });
        this.provider
            .getCurrentAnswers$()
            .pipe(untilDestroyed(this))
            .subscribe((responses) => {
                this.responses = responses;
                this.updateFormWithResponses();
            });

        this.actionsListener$
            .pipe(ofType(saveResponse.saveResponseSuccess), untilDestroyed(this))
            .subscribe((response) => {
                if (this.currentQuestion === 1) {
                    this.provider.startSurvey$(this.activeSurvey?.id);
                }
                if (this.currentQuestion === 4) {
                    this.provider.completeSurvey$(this.activeSurvey?.id);
                }
                this.provider.nextQuestion();
            });

        this.actionsListener$.pipe(ofType(dismissSurvey.dismissSurveySuccess), untilDestroyed(this)).subscribe(() => {
            this.closeButtonClicked.emit();
        });
    }

    private initializeForm(): void {
        this.surveyForm = this.fb.group({
            question1: ['', [Validators.required, this.notEmptyValidator]],
            question2: ['', [Validators.required, this.notEmptyValidator]],
            question3: ['', [Validators.required, this.notEmptyValidator]],
            question4: ['', [Validators.required, this.notEmptyValidator]],
        });
    }

    private updateFormWithResponses(): void {
        if (this.responses) {
            if (this.responses.question1) {
                this.surveyForm.get('question1').setValue(this.responses.question1);
            }
            if (this.responses.question2) {
                this.surveyForm.get('question2').setValue(this.responses.question2);
            }
            if (this.responses.question3) {
                this.surveyForm.get('question3').setValue(this.responses.question3);
            }
            if (this.responses.question4) {
                this.surveyForm.get('question4').setValue(this.responses.question4);
            }
        }
    }

    public onResponseChanged(event, response: SURVEY_QUESTION_RESPONSE_OPTIONS): void {
        this.response = response;
        if (event.checked) {
            this.surveyForm.get('question1')?.setValue(response);
        } else {
            this.surveyForm.get('question1')?.setValue(null);
        }
    }

    public saveResponse(questionNumber: number, response: string): void {
        if (response != null) {
            this.surveyForm.get(`question${questionNumber}`)?.setValue(response);
        }
        if (questionNumber === 1) {
            this.provider.dispatchSaveResponse(this.activeSurvey?.id, questionNumber, this.response);
        } else {
            this.provider.dispatchSaveResponse(this.activeSurvey?.id, questionNumber, response);
        }
    }

    public goBack(): void {
        this.provider.previousQuestion();
    }

    public onDismiss(): void {
        this.provider.dismissSurvey$(this.activeSurvey?.id);
        this.provider.resetCurrentQuestion();
    }

    public onComplete(): void {
        this.provider.resetCurrentQuestion();
        this.closeButtonClicked.emit();
    }

    private notEmptyValidator(control: UntypedFormControl): ValidationErrors | null {
        if (control.value == null || control.value.toString().trim().length === 0) {
            return { emptyField: true };
        }
        return null;
    }
}
